import { useCallback, useMemo, useState, useEffect } from 'react';
import { customCssSelector, externalFontFaceDeclarationsSelector, setCustomCss, useAppDispatch, useAppSelector } from '../../../../store';

import { useFontFaces, type FontFaceDescriptor } from './useFontFaces';
import { useFontSize, useFontWeight } from './useFontMetrics';

import { FontSize } from './FontSize';
import { FontFamily } from './FontFamily';
import { FontWeight } from './FontWeight';

import './EditStyle.scss';

function setAppFont(descriptor: { family: string, style?: string, size: string, weight: number }, dispatch: ReturnType<typeof useAppDispatch>, customCss: string, registeredFontFaces: Record<string, string>) {
  const family = descriptor.family;
  descriptor.family = family ? `'${family}', 'Poppins', sans-serif` : "'Poppins', sans-serif";

  const atFace = registeredFontFaces ? Object.values(registeredFontFaces).join('\n') : '';
  let cssRules = Object.entries(descriptor).map(([prop, value]) => `font-${prop}: ${value};`).join('\n  ');

  const baseWeight = descriptor.weight;

  if (baseWeight > 0 && !Number.isNaN(baseWeight)) {
    cssRules += '\n  ' + Object.entries({
      // Should match App.scss
      'light': 300,
      'normal': 400,
      'medium': 500,
      'bold': 700,
    }).map(([name, weight]) => {
      // Scale proportionally to requested base weight, rounded to nearest 50
      // For high weights, this may go above 1000, which is acceptable
      // since the browser treats anything above 1000 as 1000
      const newWeight = Math.round((weight * baseWeight / 400) / 50) * 50;
      return `--font-weight-${name}: ${newWeight};`;
    }).join('\n  ');
  }

  const cssText = `\n/* BEGIN EditStyle CSS */\n${atFace}\n\n#wrapper {\n  ${cssRules}\n}\n/* END EditStyle CSS */`;

  dispatch(setCustomCss(
    (customCss ?? '').replace(
      // @ts-ignore The `s` modifier, dotall
      /(((?![^\n])\n)*\/\* BEGIN EditStyle CSS \*\/.*\/\* END EditStyle CSS \*\/)?$/s,
      cssText,
    )
  ));
}

export function EditStyle() {
  const dispatch = useAppDispatch();

  const customCss = useAppSelector(customCssSelector);
  const externalFontFaceDeclarations = useAppSelector(externalFontFaceDeclarationsSelector);

  const { fontFaces, selectedFontFace } = useFontFaces();
  const selectedFontSize = useFontSize();
  const selectedFontWeight = useFontWeight();

  const [size, setSize] = useState(selectedFontSize);
  const [weight, setWeight] = useState(Number.isNaN(selectedFontWeight) ? 400 : selectedFontWeight);
  const [dropdownDescriptor, setDropdownDescriptor] = useState(selectedFontFace);

  const updateFontSettingsCallback = useCallback((descriptor: { family?: string, style?: string, size?: string, weight?: number | string }) => {
    setAppFont(
      {
        family: dropdownDescriptor.family,
        style: dropdownDescriptor.style,
        // @ts-ignore it's definitely a number
        weight: Number(dropdownDescriptor.weight ?? weight),
        size: size + 'em',
        ...descriptor,
      },
      dispatch,
      customCss,
      externalFontFaceDeclarations,
    );
  }, [dispatch, customCss, externalFontFaceDeclarations, dropdownDescriptor, size, weight]);

  // Update custom CSS whenever new fonts are added
  useEffect(() => {
    updateFontSettingsCallback({});
  }, [externalFontFaceDeclarations, updateFontSettingsCallback]);

  const enableWeightSlider = useMemo(() => fontFaces.filter((ff) => ff.family === dropdownDescriptor.family).map((ff) => ff.weight).length === 1, [fontFaces, dropdownDescriptor]);

  return (
    <>
      <hr />
      <li className="style font-settings">
        <h4>Style</h4>

        <FontSize
          size={size}
          setSize={(size: number) => {
            setSize(size);
            updateFontSettingsCallback({ size: size + 'em' });
          }}
        />

        <FontWeight
          enabled={enableWeightSlider}
          weight={weight}
          setWeight={(weight: number) => {
            setWeight(weight);
            updateFontSettingsCallback({ weight });

            if (fontFaces.find((ff) => ff.family === dropdownDescriptor.family && ff.weight?.toString() === weight.toString()) !== undefined) {
              setDropdownDescriptor({ ...dropdownDescriptor, weight: weight.toString() });
            }
          }}
        />

        <FontFamily
          selectedFontFace={dropdownDescriptor}
          setFontDescriptor={(descriptor: FontFaceDescriptor) => {
            setDropdownDescriptor(descriptor);
            updateFontSettingsCallback(descriptor);
            if (descriptor.weight) setWeight(+descriptor.weight);
          }}
        />
      </li>
    </>
  );
};
